import { RTCClient } from 'webrtc';
import {
  selectSelectedPair,
  statsEntries,
  summarizeInboundBytes,
  summarizeInboundVideo,
  type TRawStat,
} from 'telemetry/rtcStats';
import {
  recordVoipPeerCount,
  recordVoipPeerSample,
  recordVoipStatsReadFailure,
  type IVoipPeerSample,
} from 'state/session/voipQuality';

/**
 * Prod-safe per-peer `getStats()` sampler feeding the session-scoped VOIP quality counters
 * (AVS-4419, `state/session/voipQuality.ts`).
 *
 * ## Why this exists beside the recorder's sampler
 *
 * `telemetry/rtcStats.ts` already samples `getStats()` — for the Session Flight Recorder, which is
 * built only for dev and prelive (`scripts/recorder-build-gate.mjs`) and reads only while
 * `isRecording()`. The analytics counters this feeds ship from prod, where no recorder exists, so
 * they need a reader that is always present. It reuses the recorder's import-free, pure projection
 * (`selectSelectedPair`, `summarizeInboundVideo`) rather than re-implementing the four-way
 * selected-pair fallback — the same precedent as `utils/web-component.tsx` importing the
 * import-free `telemetry/recorder/gate`. Nothing here imports the recorder itself: the build check
 * greps the built artefact for a sentinel that lives only in `telemetry/recorder/core.ts`.
 *
 * On dev/prelive the two samplers run side by side (≈2N reads per ~4 s on an N-peer call). Accepted:
 * `getStats()` is a cheap asynchronous read, and consulting the recorder from here would drag its
 * sentinel into the prod bundle.
 *
 * ## The silent-transport watchdog (AVS-4441)
 *
 * The same read that feeds the counters also answers "did any byte arrive since last tick". A
 * transport that reports `connected` while delivering nothing is invisible to every state-machine
 * ladder in `webrtc/index.ts` — the prod failure delivered 10 frames and then froze with ICE
 * connected, 54 ms RTT and zero loss. After `VOIP_SILENT_TICKS_BEFORE_ESCALATION` motionless ticks
 * the sampler calls `onSilentTransport`, and the client decides what to do about it. This module
 * stays a measurement: it never restarts ICE and never writes the `transportFailed` flag.
 *
 * ## Cost and privacy
 *
 * N `getStats()` reads per `VOIP_STATS_INTERVAL_MS`, sequential, one per live peer. A peer the
 * recovery ladder wrote off (`transportFailed`) is counted but never read, so a dead peer costs
 * nothing — the ticket's N-not-N² rule. The projection keeps numbers and booleans only: the
 * selected pair's raw addresses and TURN url (`ISelectedPair`) never leave `projectVoipPeerSample`.
 *
 * ## Lifecycle
 *
 * `setTimeout`-rearmed, never `setInterval`, so a pass that outruns its interval cannot stack.
 * Armed from `initWebRtc` after a successful connect and re-armed on every `peer:connected`. Every
 * tick re-resolves `RTCClient.current()` — never a cached client, because `destroy()` nulls the
 * singleton and a takeover rebuilds it — stops itself when there is no client, and parks after
 * `VOIP_STATS_IDLE_TICKS_BEFORE_PARK` peerless ticks. Customers never emit `session:end`
 * (`utils/ui/rtc.ts`), so on their side the stop is the client going away or the idle budget.
 */

/** Healthy cadence. The ticket asks for ~5 s per peer; the recorder's 2-5 s window agrees. */
export const VOIP_STATS_INTERVAL_MS = 5_000;

/** Consecutive peerless ticks after which the loop parks itself until the next `peer:connected`. */
export const VOIP_STATS_IDLE_TICKS_BEFORE_PARK = 3;

/**
 * Consecutive ticks with NO inbound byte movement that make a transport silent (AVS-4441).
 *
 * The first tick of a window is only a baseline — it has nothing to compare against — so the
 * escalating tick is the (window + 1)th: 20 s after arming, 15 s of observed silence. Long enough
 * that a stutter, a paused track, or one slow read never spends a rung of the recovery ladder.
 */
export const VOIP_SILENT_TICKS_BEFORE_ESCALATION = 3;

/** One peer, as the sampler needs to see it. Read live per tick — never cached by this module. */
export interface IVoipStatsPeer {
  connectionId: string;
  peerConnection: RTCPeerConnection;
  /** The recovery ladder wrote this transport off — counted, never read. */
  transportFailed: boolean;
  /** `stream != null` on the store entry — `ontrack` fired. */
  hasStream: boolean;
  /** The signaled `audioMuted` flag is not `true`. */
  audioExpected: boolean;
  /** The signaled `videoMuted` flag is not `true`. */
  videoExpected: boolean;
}

/** Options for {@link createVoipStatsSampler}. Everything is injected so the loop needs no globals. */
export interface IVoipStatsSamplerOptions {
  /** Resolved LIVE, per tick. `null` means "no client" (stop); `[]` means "a client with no peers" (idle). */
  readPeers: () => IVoipStatsPeer[] | null;
  /** The number of remote peers tracked this tick, written-off ones included. */
  onPeerCount: (remotePeers: number) => void;
  onPeerSample: (connectionId: string, sample: IVoipPeerSample) => void;
  /** A `getStats()` read rejected or was not callable. */
  onReadFailure: () => void;
  /**
   * This peer's transport has delivered no bytes for a full window and needs the recovery ladder.
   *
   * Required, not optional: a sampler wired without an actor is the AVS-4441 defect itself — the
   * measurement existed and nothing acted on it. Making the sink mandatory means a future caller
   * has to decide what happens on silence rather than inherit "nothing" by omission.
   */
  onSilentTransport: (connectionId: string) => void;
  intervalMs?: number;
}

/** The sampler's control surface. */
export interface IVoipStatsSampler {
  /** Idempotent. Starts the loop, or un-parks it. */
  arm(): void;
  /** Stops the loop. A read in flight is discarded when it lands. */
  stop(): void;
  /** True while the loop is running. */
  isArmed(): boolean;
}

/**
 * Projects one peer's stats report plus its store-side flags to the sample the counters consume.
 *
 * The one place the raw report is touched. `selectSelectedPair` returns the pair's addresses and
 * TURN url; only the two candidate types (as one boolean) and the RTT survive.
 *
 * @param entries - the plain records of one `RTCStatsReport` (see `statsEntries`)
 * @param flags - the store-side facts the verdict needs, read off the peer entry
 * @returns {IVoipPeerSample} numbers and booleans only
 */
export function projectVoipPeerSample(
  entries: readonly TRawStat[],
  flags: Pick<IVoipStatsPeer, 'hasStream' | 'videoExpected'>
): IVoipPeerSample {
  const pair = selectSelectedPair(entries);

  return {
    relay: pair.localType === 'relay' || pair.remoteType === 'relay',
    roundTripMs: pair.roundTripMs,
    framesDecoded: summarizeInboundVideo(entries).framesDecoded,
    hasStream: flags.hasStream,
    videoExpected: flags.videoExpected,
  };
}

/**
 * The sampler. Pure apart from `setTimeout`; see the module header for the lifecycle it implements.
 *
 * @param options - the injected reader and sinks
 * @returns {IVoipStatsSampler} a parked sampler; call `arm()` to start it
 */
export function createVoipStatsSampler(options: IVoipStatsSamplerOptions): IVoipStatsSampler {
  const intervalMs = options.intervalMs ?? VOIP_STATS_INTERVAL_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let idleTicks = 0;
  let running = false;
  // Bumped on every `arm()`. `running` alone cannot tell two arm-eras apart: `stop()` can land
  // while a read is in flight and a later `arm()` — a fast rejoin, DCC's immediate session
  // restart, a takeover — flips it back to true before that read resolves.
  let armGeneration = 0;
  // Per-peer silence record: the last inbound byte total seen and how many ticks it has not moved.
  // Keyed by connectionId and pruned every tick against the live peer list, so a peer that leaves
  // (or rejoins) never inherits a stale window.
  const silence = new Map<string, { bytes: number; ticks: number }>();

  /**
   * Banks one tick of transport silence for a peer and says whether the window just closed.
   *
   * @param peer - the peer as the store sees it this tick
   * @param entries - its `getStats()` report, already read
   * @returns {boolean} `true` exactly once per window, on the tick that closes it
   */
  function trackSilence(peer: IVoipStatsPeer, entries: readonly TRawStat[]): boolean {
    const inbound = summarizeInboundBytes(entries);

    // Three peers legitimately receive nothing: one whose `ontrack` never fired, one that publishes
    // nothing (camera denied, send-only), and one that muted BOTH mic and camera (mute keeps the
    // inbound entries, so bytes freeze). Escalating any of them would write off a healthy peer.
    // Deleting the record makes the first unmuted tick a fresh baseline.
    if (!peer.hasStream || inbound.trackCount === 0 || (!peer.audioExpected && !peer.videoExpected)) {
      silence.delete(peer.connectionId);

      return false;
    }

    const previous = silence.get(peer.connectionId);

    if (!previous || previous.bytes !== inbound.bytes) {
      silence.set(peer.connectionId, { bytes: inbound.bytes, ticks: 0 });

      return false;
    }

    const ticks = previous.ticks + 1;

    if (ticks < VOIP_SILENT_TICKS_BEFORE_ESCALATION) {
      silence.set(peer.connectionId, { bytes: inbound.bytes, ticks });

      return false;
    }

    // Escalating drops the record rather than zeroing the counter. The next tick then has nothing
    // to compare against and starts a fresh baseline, so the actor fires once per window — never
    // once per tick for as long as the peer stays silent.
    silence.delete(peer.connectionId);

    return true;
  }

  function schedule(): void {
    if (timer !== null) {
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      const generation = armGeneration;

      // The tick's own `schedule()` is unreachable if anything in it throws — `timer` is already
      // null and every later `arm()` early-returns on `running`, so the loop would be a zombie for
      // the rest of the tab, freezing `statsSamples` and under-reporting the SESSION_ENDED
      // counters. Rescheduling here keeps a surprise from ending the loop; the era guard is the
      // same one the tick uses, so a `stop()` that raced the throw is still respected.
      void tick().catch(() => {
        if (running && armGeneration === generation) {
          schedule();
        }
      });
    }, intervalMs);
  }

  async function tick(): Promise<void> {
    if (!running) {
      return;
    }

    const generation = armGeneration;
    const peers = options.readPeers();

    if (peers === null) {
      stop();
      return;
    }

    // A peer that left the store keeps no window. Without this a rejoin — a reload, a takeover —
    // would inherit the old peer's banked silence and escalate on its very first tick.
    const livePeerIds = new Set(peers.map((peer) => peer.connectionId));

    for (const connectionId of silence.keys()) {
      if (!livePeerIds.has(connectionId)) {
        silence.delete(connectionId);
      }
    }

    if (peers.length === 0) {
      idleTicks += 1;

      if (idleTicks >= VOIP_STATS_IDLE_TICKS_BEFORE_PARK) {
        stop();
        return;
      }

      schedule();
      return;
    }

    idleTicks = 0;
    options.onPeerCount(peers.length);

    for (const peer of peers) {
      if (peer.transportFailed) {
        silence.delete(peer.connectionId);
        continue;
      }

      const entries = await readStatsEntries(peer.peerConnection);

      // `stop()` can land while a read is in flight — an uninstall, a session end, a takeover —
      // and a later `arm()` can open a new era before this read resolves. Either way the era this
      // result belongs to is gone: it belongs to nobody, and rescheduling from here would arm a
      // timer nothing owns any more. Comparing the generation, not just `running`, is what keeps
      // the old client's samples out of the new session's tally.
      if (!running || armGeneration !== generation) {
        return;
      }

      if (entries === null) {
        options.onReadFailure();
        continue;
      }

      options.onPeerSample(peer.connectionId, projectVoipPeerSample(entries, peer));

      if (trackSilence(peer, entries)) {
        options.onSilentTransport(peer.connectionId);
      }
    }

    schedule();
  }

  function stop(): void {
    running = false;
    idleTicks = 0;
    silence.clear();

    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return {
    arm(): void {
      if (running) {
        return;
      }

      running = true;
      idleTicks = 0;
      armGeneration += 1;
      schedule();
    },
    stop,
    isArmed(): boolean {
      return running;
    },
  };
}

/**
 * One `getStats()` read that reports its own failure instead of throwing.
 *
 * A pc that closed under the read rejects; a mock or a foreign object has no callable `getStats`.
 * Both are "this read failed", never "this peer is silent" — conflating the two is how a counter
 * invents an outage. Only the call itself is inside the try.
 */
async function readStatsEntries(peerConnection: RTCPeerConnection): Promise<TRawStat[] | null> {
  let report: RTCStatsReport;

  try {
    report = await peerConnection.getStats();
  } catch {
    return null;
  }

  return statsEntries(report);
}

/**
 * The live client, or `null`.
 *
 * `RTCClient.current()` rather than `getInstance()`: an observer must never be the reason a client
 * exists. The `typeof` guard is load-bearing — `utils/ui/rtc.test.ts` mocks `webrtc` as
 * `{ RTCClient: { getInstance } }` with no `current`, and a tick landing inside that suite must read
 * as "no client", never throw inside a timer callback.
 */
function currentClient(): RTCClient | null {
  const current: unknown = RTCClient.current;

  return typeof current === 'function' ? RTCClient.current() : null;
}

/**
 * The LIVE peer list, re-resolved per tick from the current client's store.
 *
 * @returns {IVoipStatsPeer[] | null} `null` when there is no client; otherwise every store entry
 * that holds a peer connection, with the three store-side facts the verdict needs
 */
export function readLiveVoipPeers(): IVoipStatsPeer[] | null {
  const client = currentClient();

  if (!client) {
    return null;
  }

  const remoteConnections = client.store.get('remoteConnections') ?? {};

  return Object.values(remoteConnections).flatMap((connection) =>
    connection.peerConnection
      ? [
          {
            connectionId: connection.connectionId,
            peerConnection: connection.peerConnection,
            transportFailed: !!connection.transportFailed,
            hasStream: !!connection.stream,
            audioExpected: connection.audioMuted !== true,
            videoExpected: connection.videoMuted !== true,
          },
        ]
      : []
  );
}

/**
 * Hands one silent transport to the recovery ladder on the live client.
 *
 * The client owns the escalation policy — how many windows a peer gets, which rung it climbs, when
 * it is written off. This module only reports that the bytes stopped. Resolving the client per call
 * (never caching it) is the same rule the tick follows: `destroy()` nulls the singleton and a
 * takeover rebuilds it.
 *
 * @param connectionId - the peer whose transport went silent
 */
export function escalateSilentTransport(connectionId: string): void {
  currentClient()?.escalateSilentTransport(connectionId);
}

/**
 * The one sampler the app runs. Module scope, like `voipHandlerUnsubscribes` in `utils/ui/rtc.ts`:
 * `initWebRtc` arms it after every successful connect and `session:end` stops it, and because it
 * re-resolves the client per tick it follows a takeover's rebuilt client on its own.
 */
export const voipStatsSampler: IVoipStatsSampler = createVoipStatsSampler({
  readPeers: readLiveVoipPeers,
  onPeerCount: recordVoipPeerCount,
  onPeerSample: recordVoipPeerSample,
  onReadFailure: recordVoipStatsReadFailure,
  onSilentTransport: escalateSilentTransport,
});
